import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { useCallback, useState } from 'react';

import { SearchInput } from '@/components/Search/SearchInput';
import { useEnvironment } from '@/cutils/context/EnvironmentContext';
import RethrownError from '@/cutils/errors/RethrownError';
import { notNullOrUndefined } from '@/cutils/misc/notNullOrUndefined';
import buildContentPageSeoConfig from '@/cutils/seo/buildContentPageSeoConfig';
import { SearchStructuredData as SearchStructuredData } from '@/cutils/seo/schema-org/buildSearchStructuredData';
import { StaticNormalizedNavigationNode } from '@/types/navigation';
import { useTracktor } from 'react-tracktor';

import { LoadingSpinner } from '../base/LoadingSpinner/LoadingSpinner';

import styles from './Search.module.scss';
import { SearchResults } from './SearchResults';
import { useSearchQuery } from './__generated__/Search.generated';
import { useDebounce } from './useDebounce';

const SEO_TITLE = 'Suche';

type Props = {
	navItem: StaticNormalizedNavigationNode;
};

export function Search({ navItem }: Props) {
	const router = useRouter();
	const searchTerm: string = (router.query.param as string) || '';
	const [inputValue, setInputValue] = useState(searchTerm);

	const {
		constants: { appUrl },
	} = useEnvironment();

	const seoUrl = `${appUrl}/suche`;
	const seoOptions = { title: SEO_TITLE, url: seoUrl };
	const seoConfig = { ...buildContentPageSeoConfig(seoOptions), noindex: true };

	const { loading, error, data, fetchMore } = useSearchQuery({
		notifyOnNetworkStatusChange: true,
		variables: { searchTerm: searchTerm, count: 10 },
	});

	const cancelDebounce = useDebounce(
		() => {
			startSearch(inputValue);
		},
		1000,
		[inputValue]
	);

	const startSearch = useCallback(
		(value: string) => {
			cancelDebounce();
			const trimmedValue = value.trim();

			if (trimmedValue !== searchTerm) {
				// replace current search query value
				router.replace({ query: { param: trimmedValue } }, undefined, { shallow: true });
			}
		},
		[cancelDebounce, router, searchTerm]
	);
	// focus input initially if search term is empty
	const autofocus = useCallback(
		(input: HTMLInputElement) => {
			if (searchTerm === '' && !!input) {
				input.focus();
			}
		},
		[searchTerm]
	);

	useTracktor({ pageViewData: { data: navItem, event: { type: 'PageViewEvent' } } });

	// this condition will be true, if the search has never been performed
	// if we are loading more results loading === true, but we already have
	// data, so we do not show the global loading spinner.
	if (loading && !data) {
		return (
			<div className={`body3 ${styles.wrapper}`}>
				<LoadingSpinner className={styles.spinner} />
			</div>
		);
	}

	if (error) {
		throw new RethrownError(error, 'SearchContainer');
	}

	if (!loading && !data?.searchResultArticles) {
		throw new Error(`No data returned from 'SearchContainer'.`);
	}

	const searchResults =
		data?.searchResultArticles?.edges
			.filter(notNullOrUndefined)
			.map((articleEdge) => articleEdge.node)
			.filter(notNullOrUndefined) ?? [];

	return (
		<>
			{/* Injects dynamic meta tags into the `<head />`. */}
			<NextSeo {...seoConfig} />

			{/* Injects script tags with structured data. */}
			<SearchStructuredData {...seoOptions} />

			<div className={`body3 ${styles.wrapper}`}>
				<SearchInput
					value={inputValue}
					onChange={(e) => {
						setInputValue(e.target.value);
					}}
					onKeyPress={({ key, currentTarget }) => {
						if (key === 'Enter') {
							// start search immediately
							startSearch(currentTarget.value);
						}
					}}
					onClear={() => {
						setInputValue('');
						// start search immediately
						startSearch('');
					}}
					ref={autofocus}
				/>

				<SearchResults
					hasNextPage={data?.searchResultArticles?.pageInfo.hasNextPage ?? false}
					onLoadMore={() => {
						fetchMore({
							variables: { cursor: data?.searchResultArticles?.pageInfo.cursor },
						});
					}}
					isLoading={loading}
					searchResultArticles={searchResults}
					totalCount={data?.searchResultArticles?.totalCount ?? null}
				/>
			</div>
		</>
	);
}
